import { useMemo, useEffect, useState } from "react"
import { useCustomerOrders } from "@app/hooks/useCustomer"
import type { OrderLineItem } from "@shopify/hydrogen-react/storefront-api-types"

const EXCLUDED_HANDLES = ["free-sample"]
const CACHE_KEY = "buyAgain:handles"
const CACHE_TTL_MS = 10 * 60 * 1000 // 10 minutes

type UsePurchaseFrequencyOptions = {
  limit?: number
}

type CachedHandles = {
  handles: string[]
  ts: number
}

const readCache = (): string[] | null => {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY)
    if (!raw) return null
    const parsed: CachedHandles = JSON.parse(raw)
    if (Date.now() - parsed.ts > CACHE_TTL_MS) {
      sessionStorage.removeItem(CACHE_KEY)
      return null
    }
    // Treat an empty cached list as a miss so we re-fetch instead of showing a sticky "No items".
    if (!parsed.handles?.length) {
      sessionStorage.removeItem(CACHE_KEY)
      return null
    }
    return parsed.handles
  } catch {
    return null
  }
}

const writeCache = (handles: string[]) => {
  // Never cache an empty result, or it sticks as "No items" until the TTL expires.
  if (!handles.length) return
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify({ handles, ts: Date.now() }))
  } catch {
    // sessionStorage might be full or unavailable
  }
}

// Invalidate the cache when the user makes a purchase so the next visit
// picks up the new order. Call this after a successful checkout redirect.
export const invalidateBuyAgainCache = () => {
  try {
    sessionStorage.removeItem(CACHE_KEY)
  } catch {
    // noop
  }
}

const usePurchaseFrequency = ({ limit }: UsePurchaseFrequencyOptions = {}) => {
  const cachedHandles = useMemo(() => (typeof window !== "undefined" ? readCache() : null), [])
  const skipFetch = !!cachedHandles

  // Reduced from 200 → 100 orders — covers most users' buying patterns
  // while cutting the Storefront API payload roughly in half.
  const { orders, loading: ordersLoading } = useCustomerOrders(skipFetch ? 0 : 100)

  const [handles, setHandles] = useState<string[]>(cachedHandles ?? [])
  const [loading, setLoading] = useState(!skipFetch)

  // Derive handles from orders once they arrive
  useEffect(() => {
    if (skipFetch || ordersLoading || !orders) return

    const frequencyMap = new Map<string, number>()

    orders.forEach((order: any) => {
      order.lineItems?.forEach((lineItem: OrderLineItem) => {
        if (!lineItem) return
        const isGift = lineItem.customAttributes?.some((a: any) => a.key === "isGift" && a.value === "true")
        const isSample = lineItem.customAttributes?.some((a: any) => a.key === "_isSample" && a.value === "true")
        const handle = lineItem.variant?.product?.handle
        if (isGift || isSample || !handle || EXCLUDED_HANDLES.includes(handle)) return

        frequencyMap.set(handle, (frequencyMap.get(handle) ?? 0) + lineItem.quantity)
      })
    })

    const sorted = Array.from(frequencyMap.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([handle]) => handle)

    writeCache(sorted)
    setHandles(sorted)
    setLoading(false)
  }, [orders, ordersLoading, skipFetch])

  const limited = useMemo(() => (limit ? handles.slice(0, limit) : handles), [handles, limit])

  return { handles: limited, loading }
}

export { usePurchaseFrequency }
